import {
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { CustomFormContext } from "../../CustomForm";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { ErrorInfo, Validation, Option } from "../../types";
import { prepareContent } from "../../helpers";
import { useTranslation } from "react-i18next";
import React from "react";

interface CustomCheckboxGroupProps<T extends Option> {
  nonForm?: boolean;
  validations?: Validation[];
  fieldId: number;
  alias: string;
  header?: string;
  fieldOptions?: readonly T[] | readonly string[] | undefined;
  defaultValue?: T | string | undefined;
  valueProp?: string;
  labelProp?: string;
  localized?: boolean;
  required?: boolean;
  readOnlyField?: boolean;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  typeId: number;
  disabledOptions?: string[];
}

const CustomCheckboxGroup = <T extends Option>({
  nonForm = false,
  alias,
  header = "",
  fieldOptions = [] as T[],
  required = false,
  readOnlyField = false,
  valueProp = undefined,
  labelProp = undefined,
  defaultValue = undefined,
  disabled = false,
  localized = false,
  className = "",
  style = {},
  disabledOptions,
}: CustomCheckboxGroupProps<T>) => {
  const {
    getValidationErrors,
    setDataDispatch,
    dataIsSet,
    errorsAreSet,
    readOnlyState,
    fieldValue,
    tempData,
    showErrors,
  } = useContext(CustomFormContext);
  const { t } = useTranslation();
  const [values, setValues] = useState<T[] | null>(null);
  const [errors, setErrors] = useState<ErrorInfo[] | null>(null);

  useEffect(() => {
    if (!dataIsSet || !errorsAreSet) {
      return;
    }
    const data = fieldValue(alias);

    if (data === null || data === undefined || data.length === 0) {
      setValues(null);
      return;
    }
    const errors = getValidationErrors(alias);
    setValues(data);
    if (errors) {
      setErrors(errors);
    }
  }, [dataIsSet, errorsAreSet, tempData]);

  useEffect(() => {
    handleSave();
  }, [values]);

  const handleSave = () => {
    const data = prepareContent(values, alias, nonForm);
    setDataDispatch({ type: "UPDATE_CONTENT", payload: data });
  };

  useEffect(() => {
    setErrors(getValidationErrors(alias));
  }, [getValidationErrors, alias]);

  const handleOnClick = (option: T) => {
    setValues((prev) => {
      if (valueProp && option.hasOwnProperty(valueProp)) {
        if (!prev) {
          return [option];
        }
        if (
          prev.find(
            (value) => (value as any)[valueProp] === (option as any)[valueProp]
          )
        ) {
          return prev.filter(
            (value) => (value as any)[valueProp] !== (option as any)[valueProp]
          );
        } else {
          return [...prev, option];
        }
      } else if (
        valueProp &&
        typeof option === "string" &&
        prev?.find((value) => (value as any)[valueProp] === option)
      ) {
        if (!prev) {
          return [option];
        }
        if (prev.find((value) => (value as any)[valueProp] === option)) {
          const prevValue = prev.find(
            (value) => (value as any)[valueProp] === option
          );
          const indexOfPrevValue = prev.indexOf(prevValue as any);
          const updatedData = [...prev];
          if (
            updatedData[indexOfPrevValue].hasOwnProperty("measurementId") &&
            (prev as any[]).find(
              (value) => ((value as any)[valueProp] === option) as any
            )?.value !== null
          ) {
            updatedData[indexOfPrevValue] = {
              ...prevValue,
              value: null,
            } as any;
          } else {
            updatedData[indexOfPrevValue] = {
              ...prevValue,
              value: defaultValue,
            } as any;
          }
          if (prevValue?.hasOwnProperty("measurementId")) {
            return [...updatedData];
          } else {
            return prev.filter((value) => (value as any)[valueProp] !== option);
          }
        } else {
          return [...prev, option];
        }
      } else {
        if (!prev) {
          return [option];
        }
        if (prev.includes(option)) {
          return prev.filter((value) => value !== option);
        }
        return [...prev, option];
      }
    });
  };

  const getLabel = (option: T | string) => {
    if (typeof option !== "string") {
      if (labelProp && option.hasOwnProperty(labelProp)) {
        return (option as any)[labelProp];
      } else if (valueProp && option.hasOwnProperty(valueProp)) {
        return (option as any)[valueProp];
      }
    } else {
      return option as string;
    }
  };

  return (
    <Grid container flexDirection="column">
      <InputLabel>
        {header}{" "}
        {(required || errors?.find((a) => a.error === "isNotNullOrEmpty")) &&
          "*"}{" "}
        {localized && (
          <Chip size="small" label={t("customform.localized")} color="info" />
        )}
      </InputLabel>
      {showErrors &&
        errors?.map((info, index) => (
          <FormHelperText key={index} variant="standard">
            <Typography variant="caption" color="red">
              {info.message}
            </Typography>
          </FormHelperText>
        ))}
      <FormGroup row>
        {fieldOptions.map((option) => {
          const value =
            valueProp && option.hasOwnProperty(valueProp)
              ? (option as any)[valueProp]
              : (option as string);

          const isExistingData =
            (
              values?.find(
                (a) => (a as any)[valueProp as string] === value
              ) as any
            )?.measurementId !== undefined;
          const included =
            values &&
            values.length > 0 &&
            (values.includes(value) ||
              (isExistingData &&
                (
                  values.find(
                    (v) => (v as any)[valueProp as string] === value
                  ) as any
                )?.value !== null));
          return readOnlyField || readOnlyState ? (
            <Grid
              item
              xs={12}
              sm={6}
              lg={4}
              key={value as string}
              className="flex-row-start"
              sx={{
                opacity: included ? 1 : 0.4,
                paddingTop: 2,
                cursor: "not-allowed",
              }}
            >
              {included ? (
                <CheckIcon color="success" />
              ) : (
                <CloseOutlined color="error" />
              )}
              <Typography
                sx={{
                  marginLeft: 1,
                  fontWeight: included ? "700" : "400",
                }}
              >
                {value as string}
              </Typography>
            </Grid>
          ) : (
            <Grid item xs={12} sm={6} lg={4} key={value as string}>
              <FormControlLabel
                disabled={
                  readOnlyField ||
                  readOnlyState ||
                  disabledOptions?.find(
                    (field) => field === (value as string)
                  ) !== undefined
                }
                control={
                  <Checkbox
                    checked={included || false}
                    onClick={() => handleOnClick(value as any)}
                    checkedIcon={<CheckIcon sx={{ color: "#26a69a" }} />}
                  />
                }
                label={getLabel(option)}
                data-testid={alias}
              />
            </Grid>
          );
        })}
      </FormGroup>
    </Grid>
  );
};

CustomCheckboxGroup.fieldType = "CustomCheckboxGroup";

export default CustomCheckboxGroup;
